import { Check, Close, ExpandMore } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  Grid,
  TableBody,
  TableHead,
  TableRow,
  Table,
  Typography,
  TableContainer,
  Tooltip,
  IconButton,
  Toolbar,
  useEventCallback,
  Divider,
} from '@mui/material';
import { AccordionSummary } from 'shell/components/shared/summary-accordion';
import {
  CancellationDueDeath,
  SubjectListForm,
} from 'cancellations/view-models/cancellation-due-death';
import { memo } from 'react';
import { noop } from 'shell/utils/noop';
import { FormikErrors, useFormikContext } from 'formik';
import { PmiTableRow } from 'shell/components/shared/table/row';
import { PmiBodyTableCell } from 'shell/components/shared/table-custom/body';
import { PmiHeadTableCell } from 'shell/components/shared/table-custom/head';
import SaveIcon from '@mui/icons-material/Save';
import RefreshIcon from '@mui/icons-material/Refresh';
import PmiTextField from 'shell/components/shared/text-field';
import { PmiTitleDivider } from 'shell/components/shared/pmi-title-divider';
import { PmiFormControl } from 'shell/components/shared/pmi-select/pmi-form-control';
import ParenthoodRelationshipList from 'shell/components/shared/parenthood-relationship-list';

interface SubjectListAccordionProps {
  expanded: boolean;
  toggle: () => void;
}

const SubjectListAccordionComponent = ({
  expanded,
  toggle,
}: SubjectListAccordionProps): React.ReactElement => {
  const formContext = useFormikContext<CancellationDueDeath>();
  const { subjectListForm, defaultSubjectListForm } = formContext.values;

  const errors: FormikErrors<SubjectListForm> | undefined = formContext.errors
    ?.subjectListForm as FormikErrors<SubjectListForm>;

  const handleFormFieldChange = useEventCallback(
    (key: keyof SubjectListForm, value: SubjectListForm[keyof SubjectListForm]) => {
      const newForm = {
        ...subjectListForm,
        [key]: value,
      };
      formContext.setFieldValue('subjectListForm', newForm);
    }
  );

  const reset = () => {
    formContext.setFieldValue('subjectListForm', defaultSubjectListForm);
  };

  const family = subjectListForm?.family || {};
  let householder = null;
  if (subjectListForm?.fiscalCodeHouseholder) {
    householder = family[subjectListForm.fiscalCodeHouseholder];
  }

  return (
    <Accordion expanded={expanded} onChange={toggle}>
      <AccordionSummary
        aria-controls="panel-subject-list-content"
        id="panel-subject-list-content"
        expandIcon={<ExpandMore />}
      >
        {!!errors ? <Close color="error" /> : <Check color="success" />}
        <Typography>
          Elenco soggetti
          <Typography style={{ color: '#d6012e', display: 'inline', paddingLeft: '5px' }}>
            {errors?.fiscalCodeHouseholder || ''}
          </Typography>
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <PmiTitleDivider>Nuovo intestatario</PmiTitleDivider>
          </Grid>
          <Grid item xs={12}>
            <Divider />
          </Grid>
          <Grid item container xs={12} spacing={2}>
            <Grid item xs={12} md={4}>
              <PmiTextField
                label="Codice fiscale"
                aria-label="Codice fiscale"
                fullWidth
                onChange={noop}
                value={householder?.fiscalCode?.toUpperCase() || ''}
                disabled
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <PmiTextField
                label="Cognome"
                aria-label="Cognome"
                fullWidth
                onChange={noop}
                value={householder?.surname || ''}
                disabled
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <PmiTextField
                label="Nome"
                aria-label="Nome"
                fullWidth
                onChange={noop}
                value={householder?.name || ''}
                disabled
              />
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <TableContainer>
              <Toolbar
                sx={{
                  pl: { sm: 2 },
                  pr: { xs: 1, sm: 1 },
                }}
              >
                <Typography
                  sx={{ flex: '1 1 100%' }}
                  variant="h6"
                  id="ElencoFamiliari"
                  component="div"
                >
                  Elenco familiari
                </Typography>
                <Tooltip arrow title="Annulla">
                  <span>
                    <IconButton
                      aria-label="Annulla"
                      aria-haspopup="true"
                      onClick={reset}
                      style={{
                        padding: '4px',
                      }}
                    >
                      <RefreshIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </Toolbar>
              <Table sx={{ minWidth: 900 }} size="medium" stickyHeader aria-label="lista soggetti">
                <TableHead>
                  <TableRow>
                    <PmiHeadTableCell>Codice fiscale</PmiHeadTableCell>
                    <PmiHeadTableCell>Cognome</PmiHeadTableCell>
                    <PmiHeadTableCell>Nome</PmiHeadTableCell>
                    <PmiHeadTableCell>Sesso</PmiHeadTableCell>
                    <PmiHeadTableCell>Data di nascita</PmiHeadTableCell>
                    <PmiHeadTableCell>Luogo di nascita</PmiHeadTableCell>
                    <PmiHeadTableCell>Residenza</PmiHeadTableCell>
                    <PmiHeadTableCell>Indirizzo</PmiHeadTableCell>
                    <PmiHeadTableCell>Parentela</PmiHeadTableCell>
                    <PmiHeadTableCell>Azioni</PmiHeadTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {/** Togliere il coniuge se muoiono entrambi, quindi morte coniuge residenti a true */}
                  {(Object.keys(family) as Array<keyof typeof family>)
                    .filter((el) => {
                      if (formContext.values.deathDataForm?.deathPartnerResident) {
                        let marriedSubject;

                        if (formContext.values.subjectData?.matrimonio) {
                          marriedSubject = formContext.values.subjectData?.matrimonio?.coniuge;
                        }

                        if (formContext.values.subjectData?.unioneCivile) {
                          marriedSubject =
                            formContext.values.subjectData?.unioneCivile?.unitoCivilmente;
                        }

                        return !(el === marriedSubject?.codiceFiscale?.codFiscale);
                      }
                      return true;
                    })
                    .map((key) => {
                      const isHeadParenthood = !!(
                        Object.keys(family) as Array<keyof typeof family>
                      )?.find((key1) => {
                        if (
                          !(
                            family[key1].fiscalCode === family[key].fiscalCode &&
                            family[key1].idSchedaSoggettoANPR ===
                              family[key].idSchedaSoggettoANPR &&
                            family[key1].idIntestatario === family[key].idIntestatario
                          )
                        ) {
                          return family[key1].relation?.toString() === '1';
                        }
                        return false;
                      });

                      return (
                        <PmiTableRow key={family[key].fiscalCode}>
                          <PmiBodyTableCell id={`${key}-table-fiscalCode`} scope="row">
                            {family[key].fiscalCode}
                          </PmiBodyTableCell>
                          <PmiBodyTableCell id={`${key}-table-surname`} scope="row">
                            {family[key].surname}
                          </PmiBodyTableCell>
                          <PmiBodyTableCell id={`${key}-table-name`} scope="row">
                            {family[key].name}
                          </PmiBodyTableCell>
                          <PmiBodyTableCell id={`${key}-table-sex`} scope="row">
                            {family[key].sex}
                          </PmiBodyTableCell>
                          <PmiBodyTableCell id={`${key}-table-birthday`} scope="row">
                            {family[key].birthday?.toFormat('dd/MM/yyyy') || ''}
                          </PmiBodyTableCell>
                          <PmiBodyTableCell id={`${key}-table-birthplace`} scope="row">
                            {family[key].birthPlace}
                          </PmiBodyTableCell>
                          <PmiBodyTableCell id={`${key}-table-residence`} scope="row">
                            {family[key].residence}
                          </PmiBodyTableCell>
                          <PmiBodyTableCell id={`${key}-table-address`} scope="row">
                            {family[key].address}
                          </PmiBodyTableCell>
                          <PmiBodyTableCell id={`${key}-table-relation`} scope="row">
                            <PmiFormControl fullWidth>
                              <ParenthoodRelationshipList
                                id={`${family[key].fiscalCode}`}
                                label=""
                                value={family[key].relation || ''}
                                onChange={(value) =>
                                  handleFormFieldChange('family', {
                                    ...family,
                                    [family[key].fiscalCode]: {
                                      ...family[family[key].fiscalCode],
                                      relation: value || '',
                                    },
                                  })
                                }
                                isHeadParenthood={isHeadParenthood}
                              />
                            </PmiFormControl>
                          </PmiBodyTableCell>
                          <PmiBodyTableCell id={`${key}-table-acions`} scope="row">
                            {!isHeadParenthood ? (
                              <Tooltip arrow title="Salva intestatario">
                                <span>
                                  <IconButton
                                    aria-label="Salva intestatario"
                                    aria-haspopup="true"
                                    onClick={() =>
                                      handleFormFieldChange(
                                        'fiscalCodeHouseholder',
                                        family[key].fiscalCode
                                      )
                                    }
                                    style={{
                                      padding: '4px',
                                    }}
                                  >
                                    <SaveIcon />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            ) : undefined}
                          </PmiBodyTableCell>
                        </PmiTableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};

SubjectListAccordionComponent.displayName = 'Subject list accordion component';

const SubjectListAccordion = memo(SubjectListAccordionComponent);

export default SubjectListAccordion;
